import { useEffect, useState } from 'react'
import { useAuth } from './auth/useAuth'
import { LoginScreen } from './auth/LoginScreen'
import { BottomNav, type Route } from './components/BottomNav'
import { LanguageToggle } from './components/LanguageToggle'
import { LogoMark } from './components/Logo'
import { HomeScreen } from './screens/HomeScreen'
import { UnitsScreen } from './screens/UnitsScreen'
import { UnitDetailScreen } from './screens/UnitDetailScreen'
import { TenantsScreen } from './screens/TenantsScreen'
import { MoreScreen } from './screens/MoreScreen'
import { RentScreen } from './screens/RentScreen'
import { ExpensesScreen } from './screens/ExpensesScreen'
import { DepositsScreen } from './screens/DepositsScreen'
import { ReportsScreen } from './screens/ReportsScreen'
import { DocumentsScreen } from './screens/DocumentsScreen'
import { BankScreen } from './screens/BankScreen'
import { SettlementScreen } from './screens/SettlementScreen'
import { RecurringChargesScreen } from './screens/RecurringChargesScreen'
import { YearScreen } from './screens/YearScreen'
import { useI18n } from './i18n/useI18n'
import { isConfigured } from './lib/supabase'
import { useRouter, tabFor } from './lib/router'
import { useYear } from './year/useYear'

/** Bandeau permanent quand le telephone perd la connexion. */
function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine)
  useEffect(() => {
    const up = () => setOnline(true)
    const down = () => setOnline(false)
    window.addEventListener('online', up)
    window.addEventListener('offline', down)
    return () => {
      window.removeEventListener('online', up)
      window.removeEventListener('offline', down)
    }
  }, [])
  return online
}

export function App() {
  const { t } = useI18n()
  const { session, profile, loading } = useAuth()
  const { path, segments, navigate, goBack } = useRouter()
  const { year, clearYear } = useYear()
  const online = useOnline()

  if (!isConfigured) {
    return (
      <div className="screen screen--centered">
        <div className="card">
          <h2>{t('config_missing_title')}</h2>
          <p>{t('config_missing_body')}</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="screen screen--centered">
        <p className="muted">{t('loading')}</p>
      </div>
    )
  }

  if (!session) return <LoginScreen />

  // Compte cree mais sans role : la RLS refuserait tout de toute facon.
  // On l'explique plutot que de montrer des ecrans vides.
  if (profile && profile.role === null) {
    return (
      <div className="screen screen--centered">
        <div className="card">
          <h2>{t('no_role_title')}</h2>
          <p>{t('no_role_body')}</p>
        </div>
      </div>
    )
  }

  // LE CHOIX DE L'ANNEE EST LE POINT D'ENTREE DE L'APPLICATION.
  //
  // Tant qu'aucune annee n'est choisie, aucun ecran de donnees n'est
  // atteignable. C'est ce qui garantit qu'aucun ecran ne melange deux
  // annees : ils lisent tous la meme annee, celle-ci.
  if (year === null) return <YearScreen />

  return (
    <div className="app">
      {!online && <div className="offlinebar">{t('offline_banner')}</div>}

      <header className="topbar">
        {/* La marque en petit, batiment et nom : elle rappelle ou l'on
            est sans prendre la place d'une ligne de donnees. */}
        <span className="topbar__brand">
          <LogoMark size={26} onDark />
          <span className="topbar__title">{t('app_name')}</span>
        </span>

        <div className="topbar__right">
          {/* L'annee consultee, toujours visible et changeable d'un
              seul appui : sans cela on finit par oublier de quelle
              annee on lit les chiffres. */}
          <button
            type="button"
            className="yearchip"
            onClick={clearYear}
            aria-label={t('year_change')}
          >
            {year}
            <span className="yearchip__caret">▾</span>
          </button>
          <LanguageToggle />
        </div>
      </header>

      <main className="app__main">{renderRoute()}</main>

      <BottomNav current={tabFor(segments) as Route} onNavigate={navigate} />
    </div>
  )

  function renderRoute() {
    // /immeuble/<id>
    if (segments[0] === 'immeuble' && segments[1]) {
      return <UnitDetailScreen unitId={segments[1]} onBack={goBack} />
    }
    if (segments[0] === 'immeuble') {
      return <UnitsScreen onOpenUnit={(id) => navigate(`/immeuble/${id}`)} />
    }
    if (segments[0] === 'locataires') return <TenantsScreen />
    if (segments[0] === 'loyers') return <RentScreen />
    if (segments[0] === 'depenses') return <ExpensesScreen />
    if (segments[0] === 'cautions') return <DepositsScreen onBack={goBack} />
    if (segments[0] === 'rapports') return <ReportsScreen />
    if (segments[0] === 'documents') return <DocumentsScreen onBack={goBack} />
    if (segments[0] === 'banque') return <BankScreen onBack={goBack} />
    if (segments[0] === 'reglement') return <SettlementScreen onBack={goBack} />
    if (segments[0] === 'charges') return <RecurringChargesScreen onBack={goBack} />
    if (segments[0] === 'plus') return <MoreScreen onNavigate={navigate} />
    if (path === '/') return <HomeScreen onNavigate={navigate} />
    return <HomeScreen onNavigate={navigate} />
  }
}
